"use client";

import { useEffect, useState, type ReactNode } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { ArrowUpRight, Menu, Search, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { Locale } from "@/i18n-config";
import { LangLink } from "@/components/locales/LangLink";
import { DocumentationLanguageSelect } from "@/components/docs/DocumentationLanguageSelect";
import { InlineText } from "@/components/content/common/InlineText";
import { inlineTextToPlainText } from "@docid/types/src/inlineText";
import type {
  DocumentationLocalizedHrefMap,
  DocumentationNavigationSection,
  DocumentationSearchResult,
  DocumentationSiteViewModel,
} from "@/utils/content/documentation/viewModel";

export type DocumentationShellDictionary = {
  brand: string;
  searchLabel: string;
  searchPlaceholder: string;
  searchEmpty: string;
  searchLoading: string;
  searchResults: string;
  searchError: string;
  openNavigation: string;
  closeNavigation: string;
  languageLabel: string;
  backToDocID: string;
};

type DocumentationSearchStatus = "idle" | "loading" | "success" | "error";

function DocumentationSearch({
  dictionary,
  onChange,
  value,
}: {
  dictionary: DocumentationShellDictionary;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className="relative block">
      <span className="sr-only">{dictionary.searchLabel}</span>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400"
      />
      <input
        aria-label={dictionary.searchLabel}
        className="h-10 w-full rounded-lg border-0 bg-zinc-100 pl-9 pr-3 text-sm text-zinc-900 outline-none ring-1 ring-inset ring-zinc-200 placeholder:text-zinc-400 focus:ring-2 focus:ring-grass-400"
        onChange={(event) => onChange(event.target.value)}
        placeholder={dictionary.searchPlaceholder}
        type="search"
        value={value}
      />
    </label>
  );
}

function DocumentationSearchResults({
  dictionary,
  onNavigate,
  query,
  results,
  status,
}: {
  dictionary: DocumentationShellDictionary;
  onNavigate: () => void;
  query: string;
  results: DocumentationSearchResult[];
  status: DocumentationSearchStatus;
}) {
  if (query.trim().length < 2) {
    return null;
  }

  return (
    <section aria-label={dictionary.searchResults}>
      <h2 className="sr-only">{dictionary.searchResults}</h2>
      {status === "loading" && (
        <p className="px-4 py-5 text-sm text-zinc-500">
          {dictionary.searchLoading}
        </p>
      )}
      {status === "error" && (
        <p className="px-4 py-5 text-sm text-rose-500">
          {dictionary.searchError}
        </p>
      )}
      {status === "success" && results.length === 0 && (
        <p className="px-4 py-5 text-sm text-zinc-500">
          {dictionary.searchEmpty}
        </p>
      )}
      {status === "success" && results.length > 0 && (
        <ul className="divide-y divide-zinc-100">
          {results.map((result) => (
            <li key={result.id}>
              <Link
                className="block px-4 py-3 transition-colors hover:bg-grass-100/25"
                href={result.href}
                onClick={onNavigate}
              >
                <span className="block text-sm font-semibold text-zinc-900">
                  <InlineText value={result.title} />
                </span>
                {result.description && (
                  <span className="mt-1 line-clamp-2 block text-xs leading-5 text-zinc-500">
                    <InlineText value={result.description} />
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DocumentationNavigation({
  currentPathname,
  dictionary,
  navigation,
  onNavigate,
}: {
  currentPathname: string;
  dictionary: DocumentationShellDictionary;
  navigation: DocumentationNavigationSection[];
  onNavigate?: () => void;
}) {
  if (navigation.length === 0) {
    return <p className="text-sm text-zinc-500">{dictionary.searchEmpty}</p>;
  }

  return (
    <nav aria-label={dictionary.openNavigation}>
      <ul className="space-y-9">
        {navigation.map((section) => (
          <li key={section.id}>
            <h2 className="text-sm font-semibold text-zinc-950">
              <InlineText value={section.title} />
            </h2>
            <ul className="mt-4 space-y-3 border-l-2 border-zinc-200">
              {section.links.map((link) => {
                const isCurrent =
                  currentPathname.replace(/\/+$/, "") ===
                  link.href.replace(/\/+$/, "");

                return (
                  <li className="relative" key={link.id}>
                    <Link
                      aria-current={isCurrent ? "page" : undefined}
                      className={[
                        "before:absolute before:-left-1 before:top-1/2 before:size-1.5 before:-translate-y-1/2 before:rounded-full",
                        "block pl-4 text-sm leading-5 transition-colors",
                        isCurrent
                          ? "font-semibold text-grass-700 before:bg-grass-500"
                          : "text-zinc-500 before:hidden before:bg-zinc-300 hover:text-zinc-900 hover:before:block",
                      ].join(" ")}
                      href={link.href}
                      onClick={onNavigate}
                    >
                      <InlineText value={link.title} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function DocumentationShell({
  children,
  dictionary,
  homeHref,
  lang,
  localizedHrefsByPageId,
  site,
}: {
  children: ReactNode;
  dictionary: DocumentationShellDictionary;
  homeHref: string;
  lang: Locale;
  localizedHrefsByPageId: DocumentationLocalizedHrefMap;
  site: DocumentationSiteViewModel;
}) {
  const pathname = usePathname();
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<
    DocumentationSearchResult[]
  >([]);
  const [searchStatus, setSearchStatus] =
    useState<DocumentationSearchStatus>("idle");

  useEffect(() => {
    const query = search.trim();

    if (query.length < 2) {
      setSearchResults([]);
      setSearchStatus("idle");
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setSearchStatus("loading");

      try {
        const params = new URLSearchParams({ lang, q: query });
        const response = await fetch(`/api/docs/search?${params.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Documentation search failed.");
        }

        const payload = (await response.json()) as {
          results?: DocumentationSearchResult[];
        };
        setSearchResults(payload.results ?? []);
        setSearchStatus("success");
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        setSearchResults([]);
        setSearchStatus("error");
      }
    }, 200);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [lang, search]);

  useEffect(() => {
    setIsNavigationOpen(false);
    setSearch("");
  }, [pathname]);

  const handleSearchNavigate = () => {
    setIsNavigationOpen(false);
    setSearch("");
  };
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";
  const currentPageId =
    normalizedPathname === (homeHref.replace(/\/+$/, "") || "/")
      ? site.homePageId
      : (site.navigation
          .flatMap((section) => section.links)
          .find(
            (link) =>
              (link.href.replace(/\/+$/, "") || "/") === normalizedPathname,
          )?.id ?? null);
  const localizedHrefs = currentPageId
    ? localizedHrefsByPageId[currentPageId]
    : undefined;

  return (
    <div className="min-h-screen w-full bg-white font-jost text-zinc-900">
      <header className="sticky top-0 z-40 border-t-4 border-grass-500 bg-white/95 shadow-sm shadow-zinc-950/5 backdrop-blur">
        <div className="mx-auto flex h-[4.75rem] max-w-[90rem] items-center gap-4 px-4 sm:px-6 lg:px-8 xl:px-12">
          <button
            aria-label={dictionary.openNavigation}
            className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 lg:hidden"
            onClick={() => setIsNavigationOpen(true)}
            type="button"
          >
            <Menu className="size-6" />
          </button>
          <Link
            aria-label={inlineTextToPlainText(site.title)}
            className="flex min-w-0 items-center gap-4"
            href={homeHref}
          >
            <Image
              alt={inlineTextToPlainText(dictionary.brand)}
              className="h-auto w-[128px] sm:w-[141px]"
              height={28}
              priority
              src="/images/logo-docid.svg"
              width={141}
            />
            <span
              aria-hidden="true"
              className="hidden h-7 w-px bg-zinc-200 sm:block"
            />
            <span className="hidden truncate text-sm font-semibold text-zinc-600 sm:block">
              <InlineText value={site.title} />
            </span>
          </Link>
          <div className="relative ml-auto hidden w-full max-w-xs lg:block">
            <DocumentationSearch
              dictionary={dictionary}
              onChange={setSearch}
              value={search}
            />
            {search.trim().length >= 2 && (
              <div className="absolute left-0 right-0 top-12 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl shadow-zinc-950/10">
                <DocumentationSearchResults
                  dictionary={dictionary}
                  onNavigate={handleSearchNavigate}
                  query={search}
                  results={searchResults}
                  status={searchStatus}
                />
              </div>
            )}
          </div>
          <LangLink
            className="ml-auto hidden items-center gap-1.5 text-sm font-semibold text-zinc-600 transition-colors hover:text-grass-700 lg:flex"
            href="/"
            lang={lang}
          >
            <InlineText value={dictionary.backToDocID} />
            <ArrowUpRight className="size-4" />
          </LangLink>
        </div>
      </header>

      <div className="relative mx-auto flex w-full max-w-[90rem]">
        <aside className="hidden w-72 flex-none border-r border-zinc-200 bg-zinc-50/80 lg:block">
          <div className="sticky top-20 h-[calc(100vh-5rem)] overflow-y-auto px-8 py-14">
            <DocumentationLanguageSelect
              label={dictionary.languageLabel}
              lang={lang}
              localizedHrefs={localizedHrefs}
            />
            <div className="mt-10">
              <DocumentationNavigation
                currentPathname={pathname}
                dictionary={dictionary}
                navigation={site.navigation}
              />
            </div>
          </div>
        </aside>
        <div className="flex min-w-0 flex-1 justify-center px-4 sm:px-6 lg:px-8 xl:px-12">
          {children}
        </div>
      </div>

      <Dialog
        aria-label={dictionary.openNavigation}
        className="relative z-50 lg:hidden"
        onClose={setIsNavigationOpen}
        open={isNavigationOpen}
      >
        <div className="fixed inset-0 bg-zinc-950/40 backdrop-blur-sm" />
        <div className="fixed inset-0 flex pr-10">
          <DialogPanel className="min-h-full w-full max-w-sm overflow-y-auto bg-white px-5 pb-10 pt-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <Link
                aria-label={inlineTextToPlainText(site.title)}
                href={homeHref}
              >
                <Image
                  alt={inlineTextToPlainText(dictionary.brand)}
                  height={28}
                  priority
                  src="/images/logo-docid.svg"
                  width={141}
                />
              </Link>
              <button
                aria-label={dictionary.closeNavigation}
                className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                onClick={() => setIsNavigationOpen(false)}
                type="button"
              >
                <X className="size-6" />
              </button>
            </div>
            <div className="mt-7">
              <DocumentationSearch
                dictionary={dictionary}
                onChange={setSearch}
                value={search}
              />
            </div>
            <div className="mt-6">
              <DocumentationLanguageSelect
                label={dictionary.languageLabel}
                lang={lang}
                localizedHrefs={localizedHrefs}
              />
            </div>
            <div className="mt-8">
              {search.trim().length >= 2 ? (
                <DocumentationSearchResults
                  dictionary={dictionary}
                  onNavigate={handleSearchNavigate}
                  query={search}
                  results={searchResults}
                  status={searchStatus}
                />
              ) : (
                <DocumentationNavigation
                  currentPathname={pathname}
                  dictionary={dictionary}
                  navigation={site.navigation}
                  onNavigate={() => setIsNavigationOpen(false)}
                />
              )}
            </div>
            <LangLink
              className="mt-10 flex items-center gap-1.5 border-t border-zinc-200 pt-6 text-sm font-semibold text-zinc-600 hover:text-grass-700"
              href="/"
              lang={lang}
            >
              <InlineText value={dictionary.backToDocID} />
              <ArrowUpRight className="size-4" />
            </LangLink>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
